import cookies from 'js-cookie'

export const AUTH_COOKIE_NAME = 'EWS_AUTH_KEY'

// Persist the auth cookie for 30 days instead of writing a session cookie.
// A session cookie dies when the browser process exits, so quitting the
// browser (or rebooting) forced a full Okta round-trip every morning even
// though the stored Okta refresh token was still valid. The JWT inside is
// still short-lived and independently verified on every request — this only
// controls how long the browser holds onto it.
export const AUTH_COOKIE_DAYS = 30

export function authCookieDomain() {
  return `.${location.hostname.split('.').slice(-2).join('.')}`
}

/**
 * A usable bearer token: a non-empty string with the three dot-separated JWT
 * segments. js-cookie serialises anything else with String(), so writing
 * undefined stores the literal "undefined"; every request in every tab then
 * fails with 401 and the user is forced back through login (ND-2362).
 */
export function isAuthToken(value) {
  if (typeof value !== 'string') return false
  const segments = value.split('.')
  return segments.length === 3 && segments.every(Boolean)
}

/**
 * Write the shared auth cookie. Throws on a malformed token so callers treat
 * it as a failed refresh and the previous cookie stays in place.
 *
 * @param {string} jwt
 * @param {object} [options] extra js-cookie options (e.g. sameSite for iframes).
 *   Pass { session: true } for a session cookie (tokenized share-link access).
 */
export function setAuthCookie(jwt, options = {}) {
  if (!isAuthToken(jwt)) {
    const got = jwt === undefined ? 'undefined' : jwt === null ? 'null' : typeof jwt
    throw new TypeError(`Refusing to write ${AUTH_COOKIE_NAME}: expected a JWT, got ${got}`)
  }

  const { session = false, ...cookieOptions } = options
  cookies.set(AUTH_COOKIE_NAME, jwt, {
    domain: authCookieDomain(),
    path: '/',
    // js-cookie skips falsy attributes, so undefined here yields a session cookie.
    expires: session ? undefined : AUTH_COOKIE_DAYS,
    secure: true,
    ...cookieOptions
  })
}

export function getAuthCookie() {
  return cookies.get(AUTH_COOKIE_NAME)
}

/**
 * Remove the shared auth cookie. Must match the domain/path used to set it,
 * otherwise the browser keeps the original cookie.
 */
export function removeAuthCookie() {
  cookies.remove(AUTH_COOKIE_NAME, { domain: authCookieDomain(), path: '/' })
}
